import { useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { WaypointKind } from '@xingroute/schema'
import { WaypointGlyph, type WaypointGlyphKind } from '../ui/IconButton.js'
import { WAYPOINT_KINDS } from '../../lib/waypointKinds.js'
import { usePopoverAnchor } from '../usePopoverAnchor.js'

export interface WaypointKindPickerProps {
  open: boolean
  current?: WaypointKind
  onSelect: (kind: WaypointKind) => void
  onClose: () => void
  anchorRef?: React.RefObject<HTMLElement | null>
  anchorKey?: string | null
  presentation?: 'popover' | 'modal'
}

export function WaypointKindPicker({
  open,
  current,
  onSelect,
  onClose,
  anchorRef,
  anchorKey,
  presentation = 'popover',
}: WaypointKindPickerProps) {
  const { t } = useTranslation('common')
  const popRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useMemo(
    () => ({ current: anchorRef?.current ?? null }) as React.RefObject<HTMLElement>,
    [anchorRef?.current, anchorKey],
  )
  const anchor = usePopoverAnchor(triggerRef, open && presentation === 'popover', { gap: 4 })

  useEffect(() => {
    if (presentation !== 'popover' || !open) return
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onClose, presentation])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const kinds = WAYPOINT_KINDS

  if (presentation === 'modal') {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--xr-overlay-bg)] p-4"
        role="dialog"
        aria-modal="true"
        aria-label={t('editor.waypointKind.pickerLabel')}
        onClick={onClose}
        data-testid="waypoint-kind-modal"
      >
        <div
          className="w-full max-w-sm rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 shadow-[var(--xr-shadow-md)]"
          onClick={(event) => event.stopPropagation()}
        >
          <h2 className="mb-3 text-sm font-semibold text-[var(--xr-text-primary)]">
            {t('editor.waypointKind.pickerLabel')}
          </h2>
          <div
            role="listbox"
            aria-label={t('editor.waypointKind.pickerLabel')}
            className="grid grid-cols-3 gap-1.5"
          >
            {kinds.map((kind) => (
              <button
                key={kind}
                type="button"
                role="option"
                aria-selected={kind === current}
                onClick={() => {
                  onSelect(kind)
                  onClose()
                }}
                className={`flex flex-col items-center gap-0.5 rounded p-1.5 text-[10px] leading-tight hover:bg-[var(--xr-bg-surface-elev)] ${kind === current ? 'bg-[var(--xr-brand-primary-soft)] font-medium' : ''}`}
              >
                <WaypointGlyph kind={kind as WaypointGlyphKind} size={18} />
                <span className="truncate text-center">{t(`waypointKind.${kind}`)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!anchor) return null

  return createPortal(
    <div
      ref={popRef}
      className="fixed z-50 rounded-lg border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-2 shadow-lg"
      style={{ top: anchor.top, left: anchor.left, minWidth: 200 }}
      role="listbox"
      aria-label={t('editor.waypointKind.pickerLabel')}
    >
      <div className="grid grid-cols-3 gap-1">
        {kinds.map((kind) => (
          <button
            key={kind}
            type="button"
            role="option"
            aria-selected={kind === current}
            onClick={() => {
              onSelect(kind)
              onClose()
            }}
            className={`flex flex-col items-center gap-0.5 rounded p-1.5 text-[10px] leading-tight hover:bg-[var(--xr-bg-surface-elev)] ${kind === current ? 'bg-[var(--xr-brand-primary-soft)] font-medium' : ''}`}
          >
            <WaypointGlyph kind={kind as WaypointGlyphKind} size={18} />
            <span className="truncate text-center">{t(`waypointKind.${kind}`)}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  )
}
